import { apiClient } from "./client";
import type {
  ExtractionJobResponse,
  JobStatusResponse,
  ResolveResponse,
  ImportResultResponse,
  SessionStatusResponse,
  TokenResolution,
  TokenMatch,
  UnmatchedToken,
  TokenPositions,
  ReconstructedEntry,
} from "@/components/timetable-import/typesV2";
import type { TokenResolutionContext } from "@/types/tokenResolution";

export function isImportCancelled(error: unknown): boolean {
  const name = (error as { name?: string })?.name;
  const code = (error as { code?: string })?.code;
  return (
    name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED"
  );
}

function importAbortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

function interruptibleDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(importAbortError());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(importAbortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export const timetableImportV2Api = {
  async extract(
    fileId: string,
    templateId?: string
  ): Promise<ExtractionJobResponse> {
    const response = await apiClient.post(
      "/api/v1/timetables/v2/extract",
      {
        uploaded_file_id: fileId,
        template_id: templateId || null,
      },
      { timeout: 120000 }
    );
    return response.data;
  },

  async getJobStatus(
    jobId: string,
    signal?: AbortSignal
  ): Promise<JobStatusResponse> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/jobs/${jobId}/status`,
      { signal, timeout: 120000 }
    );
    return response.data;
  },

  async rememberIgnoredTokens(
    tokens: string[],
    templateId?: string | null
  ): Promise<{ tokens: string[] }> {
    const response = await apiClient.post(
      "/api/v1/timetables/v2/ignore-tokens",
      {
        tokens,
        template_id: templateId || null,
      },
      { timeout: 300000 }
    );
    return response.data;
  },

  async resolveTokens(
    sessionId: string,
    resolutions: TokenResolution[],
    orphanPatterns?: Record<string, unknown>[],
    resolutionContexts?: TokenResolutionContext[]
  ): Promise<ResolveResponse> {
    const payload: Record<string, unknown> = {
      session_id: sessionId,
      token_resolutions: resolutions,
    };
    if (orphanPatterns) {
      payload.orphan_patterns = orphanPatterns;
    }
    if (resolutionContexts && resolutionContexts.length > 0) {
      payload.resolution_contexts = resolutionContexts;
    }

    // Debug: log what's being sent (always log, not just in DEV)
    console.log("[timetableImportV2] resolveTokens payload:", {
      sessionId,
      resolutionsCount: resolutions.length,
      confirmedCount: resolutions.filter((r) => r.confirmed).length,
      splitCount: resolutions.filter((r) => r.split).length,
      hasOrphanPatterns: !!orphanPatterns,
      resolutions: resolutions.slice(0, 5), // First 5 resolutions for debugging
    });

    const response = await apiClient.post(
      "/api/v1/timetables/v2/resolve-tokens",
      payload,
      {
        timeout: 300000,
      }
    );
    this.invalidateTokenStatusCache();
    return response.data;
  },

  async import(
    sessionId: string,
    entries?: ReconstructedEntry[],
    mode = "all",
    validFrom?: string | null,
    validTo?: string | null,
    signal?: AbortSignal
  ): Promise<ImportResultResponse> {
    const payload: Record<string, unknown> = {
      session_id: sessionId,
      import_mode: mode,
    };
    if (entries && entries.length > 0) {
      payload.entries = entries;
    }
    if (validFrom !== undefined) {
      payload.valid_from = validFrom;
    }
    if (validTo !== undefined) {
      payload.valid_to = validTo;
    }
    const response = await apiClient.post(
      "/api/v1/timetables/v2/import",
      payload,
      {
        timeout: 300000,
        signal,
      }
    );
    this.invalidateTokenStatusCache();
    return response.data;
  },

  async getSession(sessionId: string): Promise<SessionStatusResponse> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/sessions/${sessionId}`
    );
    return response.data;
  },

  async cancelSession(
    sessionId: string
  ): Promise<{ message: string; session_id: string }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/sessions/${sessionId}/cancel`
    );
    return response.data;
  },

  async cancelJob(jobId: string): Promise<{ message: string; job_id: string }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/jobs/${jobId}/cancel`
    );
    return response.data;
  },

  async getExtractionResult(sessionId: string): Promise<{
    session_id: string;
    matched_tokens: TokenMatch[];
    unmatched_tokens: UnmatchedToken[];
    token_positions: Record<string, TokenPositions>;
    orphan_patterns: Array<{
      regex: string;
      field: string;
      match_mode: string;
    }>;
    split_tracking: Record<string, string>;
    has_unmatched: boolean;
    matched_count: number;
    unmatched_count: number;
    irrelevant_count: number;
    ignored_tokens_count: number;
    total_tokens: number;
    active_time_slots: string[];
    unmatched_header_codes: string[];
    updated_template_id: string | null;
  }> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/sessions/${sessionId}/extraction-result`,
      { timeout: 300000 }
    );
    return response.data;
  },

  async getResolvedEntries(sessionId: string): Promise<{
    session_id: string;
    reconstructed_entries: ReconstructedEntry[];
    status: string;
  }> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/sessions/${sessionId}/resolved-entries`
    );
    return response.data;
  },

  // --- Batch operations ---

  async listOpenBatches(): Promise<{
    batches: Array<{
      batch_id: string;
      uploaded_file_id: string;
      filename: string | null;
      status: string;
      total_sections: number;
      resolved_sections: number;
      imported_sections: number;
      failed_sections: number;
      created_at: string | null;
      completed_at: string | null;
    }>;
    total: number;
  }> {
    const response = await apiClient.get("/api/v1/timetables/v2/batches", {
      params: { open_only: true, page: 1, page_size: 50 },
    });
    return response.data;
  },

  async getBatchStatus(batchId: string): Promise<{
    batch_id: string;
    status: string;
    total_sections: number;
    resolved_sections: number;
    imported_sections: number;
    failed_sections: number;
    extraction_metadata: Record<string, unknown> | null;
    sections: Array<{
      section_index: number;
      section_identifier: string | null;
      section_type: string | null;
      section_pages: number[];
      session_id: string | null;
      status: string;
      error: string | null;
      has_unmatched: boolean | null;
      matched_count: number | null;
      unmatched_count: number | null;
    }>;
    created_at: string | null;
    completed_at: string | null;
    paused_at_section: number | null;
    pending_teachers?: string[] | null;
    pending_entries?: number | null;
    error?: string | null;
    skipped_sections?: number;
  }> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/batches/${batchId}/status`,
      { timeout: 120000 }
    );
    return response.data;
  },

  async importAllSections(
    batchId: string,
    importMode = "resolved_only",
    onProgress?: (update: {
      progress: number;
      message: string;
      pagesDone: number;
      pagesTotal: number;
    }) => void,
    signal?: AbortSignal
  ): Promise<{
    batch_id: string;
    total_sections: number;
    imported_sections: number;
    skipped_sections: number;
    failed_sections: number;
    errors: Array<{ section_index: number; session_id: string; error: string }>;
    teachers_linked_auto: number;
    teacher_codes_needing_review: string[];
    pending_teachers: string[];
    pending_entries_count: number;
  }> {
    const started = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/import-all`,
      {
        import_mode: importMode,
      },
      { timeout: 120000 }
    );
    const jobId = started.data?.job_id as string | undefined;
    const cancelRunningJob = async () => {
      if (!jobId) return;
      try {
        await this.cancelJob(jobId);
      } catch {
        // ignore
      }
    };
    const waitUntilStopped = async () => {
      if (!jobId) return null;
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        const status = await this.getJobStatus(jobId);
        onProgress?.({
          progress: status.progress || 0,
          message: "Wird abgebrochen…",
          pagesDone:
            typeof status.pages_done === "number" ? status.pages_done : 0,
          pagesTotal:
            typeof status.pages_total === "number" ? status.pages_total : 0,
        });
        if (
          status.status === "completed" ||
          status.status === "cancelled" ||
          status.status === "failed"
        ) {
          return status;
        }
        await interruptibleDelay(1000);
      }
      throw new Error("Import-Timeout");
    };
    if (signal?.aborted) {
      await cancelRunningJob();
      const status = jobId ? await waitUntilStopped() : null;
      if (status?.status === "completed" && status.result) {
        return status.result as {
          batch_id: string;
          total_sections: number;
          imported_sections: number;
          skipped_sections: number;
          failed_sections: number;
          errors: Array<{
            section_index: number;
            session_id: string;
            error: string;
          }>;
          teachers_linked_auto: number;
          teacher_codes_needing_review: string[];
          pending_teachers: string[];
          pending_entries_count: number;
        };
      }
      throw importAbortError();
    }
    if (!jobId) {
      return started.data;
    }
    const onAbort = () => {
      void cancelRunningJob();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    try {
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        if (signal?.aborted) throw importAbortError();
        const status = await this.getJobStatus(jobId, signal);
        onProgress?.({
          progress: status.progress || 0,
          message: status.message || "",
          pagesDone:
            typeof status.pages_done === "number" ? status.pages_done : 0,
          pagesTotal:
            typeof status.pages_total === "number" ? status.pages_total : 0,
        });
        if (status.status === "completed" && status.result) {
          return status.result as {
            batch_id: string;
            total_sections: number;
            imported_sections: number;
            skipped_sections: number;
            failed_sections: number;
            errors: Array<{
              section_index: number;
              session_id: string;
              error: string;
            }>;
            teachers_linked_auto: number;
            teacher_codes_needing_review: string[];
            pending_teachers: string[];
            pending_entries_count: number;
          };
        }
        if (status.status === "cancelled") throw importAbortError();
        if (status.status === "failed") {
          const message = status.error || "Import fehlgeschlagen";
          if (message.includes("offene Tokens")) {
            const error = new Error(message) as Error & {
              response: { status: number; data: { detail: string } };
            };
            error.response = { status: 409, data: { detail: message } };
            throw error;
          }
          throw new Error(message);
        }
        await interruptibleDelay(1000, signal);
      }
      throw new Error("Import-Timeout");
    } catch (error) {
      if (signal?.aborted || isImportCancelled(error)) {
        await cancelRunningJob();
        const status = await waitUntilStopped();
        if (status?.status === "completed" && status.result) {
          return status.result as {
            batch_id: string;
            total_sections: number;
            imported_sections: number;
            skipped_sections: number;
            failed_sections: number;
            errors: Array<{
              section_index: number;
              session_id: string;
              error: string;
            }>;
            teachers_linked_auto: number;
            teacher_codes_needing_review: string[];
            pending_teachers: string[];
            pending_entries_count: number;
          };
        }
        throw importAbortError();
      }
      throw error;
    } finally {
      signal?.removeEventListener("abort", onAbort);
    }
  },

  async cancelBatch(
    batchId: string
  ): Promise<{ message: string; batch_id: string }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/cancel`
    );
    return response.data;
  },

  async resolveAllSections(
    batchId: string,
    globalResolutions: TokenResolution[],
    perSectionResolutions: Record<number, TokenResolution[]> = {}
  ): Promise<{
    batch_id: string;
    resolved_count: number;
    errors: Array<{ section_index: number; error: string }>;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/resolve-all`,
      {
        global_resolutions: globalResolutions,
        per_section_resolutions: perSectionResolutions,
      },
      {
        timeout: 300000,
      }
    );
    return response.data;
  },

  async autoResolveAllSections(batchId: string): Promise<{
    batch_id: string;
    resolved_count: number;
    errors: Array<{ section_index: number; error: string }>;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/auto-resolve-all`,
      null,
      {
        timeout: 300000,
      }
    );
    return response.data;
  },

  // --- Orphaned-fix-v2: Teacher Assignment operations ---

  async getUserSuggestions(
    batchId: string,
    teacherCode: string
  ): Promise<{
    teacher_code: string;
    suggestions: Array<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      confidence: number;
      teacher_code?: string | null;
    }>;
  }> {
    // Add timestamp to prevent caching and get fresh filtering results
    const timestamp = Date.now();
    const response = await apiClient.get(
      `/api/v1/timetables/v2/batches/${batchId}/user-suggestions/${encodeURIComponent(teacherCode)}?t=${timestamp}`
    );
    return response.data;
  },

  async assignTeacher(
    batchId: string,
    teacherCode: string,
    userId?: string,
    createUserEmail?: string
  ): Promise<{
    teacher_id: string;
    user_id: string;
    entries_imported: number;
    entries_skipped: number;
    remaining_pending: number;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/assign-teacher`,
      {
        teacher_code: teacherCode,
        user_id: userId || null,
        create_user_email: createUserEmail || null,
      }
    );
    return response.data;
  },

  async skipTeacher(
    batchId: string,
    teacherCode: string
  ): Promise<{
    skipped: string;
    remaining_pending: number;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/skip-teacher`,
      {
        teacher_code: teacherCode,
      }
    );
    return response.data;
  },

  async skipAllPending(batchId: string): Promise<{
    skipped_count: number;
    batch_status: string;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/skip-all-pending`
    );
    return response.data;
  },

  async getBatchPendingStatus(batchId: string): Promise<{
    batch_id: string;
    status: string;
    pending_teachers: string[];
    pending_entries_count: number;
    per_teacher_counts: Record<string, number>;
    per_teacher_sessions: Record<
      string,
      {
        session_id: string;
        section_pages: number[];
        section_identifier: string | null;
      }
    >;
  }> {
    const response = await apiClient.get(
      `/api/v1/timetables/v2/batches/${batchId}/pending-status`,
      { timeout: 120000 }
    );
    return response.data;
  },

  async deleteSessionEntries(
    sessionId: string,
    indices: number[]
  ): Promise<{
    message: string;
    remaining_count: number;
  }> {
    const params = indices.map((i) => `indices=${i}`).join("&");
    const response = await apiClient.delete(
      `/api/v1/timetables/v2/sessions/${sessionId}/entries?${params}`
    );
    return response.data;
  },

  async retrySession(sessionId: string): Promise<{
    job_id: string;
    session_id: string;
    status: string;
    message: string;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/sessions/${sessionId}/retry`
    );
    return response.data;
  },

  async listOrphanedTeachers(): Promise<{
    orphaned_teachers: Array<{
      id: string;
      teacher_code: string | null;
      email: string;
      first_name: string;
      last_name: string;
      timetable_entries: number;
      created_at: string | null;
    }>;
    total: number;
  }> {
    const response = await apiClient.get("/api/v1/admin/orphaned-teachers");
    return response.data;
  },

  async cleanupOrphanedTeachers(
    teacherIds?: string[],
    dryRun = true
  ): Promise<{
    dry_run: boolean;
    deleted_count: number;
    entries_deleted: number;
    teachers: Array<{
      id: string;
      teacher_code: string | null;
      email: string;
      entries: number;
    }>;
  }> {
    const response = await apiClient.post(
      "/api/v1/admin/orphaned-teachers/cleanup",
      {
        teacher_ids: teacherIds || null,
        dry_run: dryRun,
      }
    );
    return response.data;
  },

  // --- Token Import Log operations (token-duplicate-v1) ---

  _tokenStatusCache: new Map<string, { data: unknown; timestamp: number }>(),
  _tokenStatusCacheTTL: 60 * 1000,

  async getTokenStatus(templateId: string): Promise<{
    template_id: string;
    token_status: Record<
      string,
      {
        field: string;
        confirmed: boolean;
        last_action?: string;
        last_batch_id?: string;
        last_at?: string;
        composed_from?: string[];
        source?: string;
        split_into?: string[]; // Parts this token was split into
        is_split?: boolean;
        auto_split?: boolean;
      }
    >;
    config_size_bytes?: number;
    total_tokens?: number;
    tokens_per_field?: Record<string, number>;
    size_warning?: boolean;
    total?: number;
    has_more?: boolean;
    next_offset?: number | null;
  }> {
    // TEMP: Cache disabled for debugging
    const response = await apiClient.get(
      `/api/v1/timetables/v2/templates/${templateId}/token-status`
    );
    return response.data;
  },

  invalidateTokenStatusCache(templateId?: string) {
    if (templateId) {
      this._tokenStatusCache.delete(templateId);
    } else {
      this._tokenStatusCache.clear();
    }
  },

  async getImportLog(
    templateId: string,
    params?: {
      batch_id?: string;
      token?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    template_id: string;
    entries: Array<{
      id: string;
      template_id: string;
      batch_id?: string;
      action: string;
      token: string;
      field?: string;
      previous_state?: Record<string, unknown>;
      new_state?: Record<string, unknown>;
      composed_from?: string[];
      source: string;
      section_index?: number;
      created_at: string;
      created_by_id?: string;
    }>;
    total: number;
    has_more: boolean;
  }> {
    const query = new URLSearchParams();
    if (params?.batch_id) query.set("batch_id", params.batch_id);
    if (params?.token) query.set("token", params.token);
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    const response = await apiClient.get(
      `/api/v1/timetables/v2/templates/${templateId}/import-log?${query.toString()}`
    );
    return response.data;
  },

  async checkBatchTokenStatus(
    batchId: string,
    tokens: string[]
  ): Promise<{
    results: Array<{
      token: string;
      status?: {
        field: string;
        confirmed: boolean;
        last_action?: string;
        last_batch_id?: string;
        last_at?: string;
        composed_from?: string[];
        source?: string;
      };
      is_new: boolean;
      is_confirmed: boolean;
      is_ignored: boolean;
    }>;
    new_tokens: string[];
    confirmed_tokens: string[];
    ignored_tokens: string[];
    known_tokens: Record<
      string,
      {
        field: string;
        confirmed: boolean;
        last_action?: string;
        last_batch_id?: string;
        last_at?: string;
        composed_from?: string[];
        source?: string;
      }
    >;
  }> {
    const response = await apiClient.post(
      `/api/v1/timetables/v2/batches/${batchId}/check-token-status`,
      {
        tokens,
      }
    );
    return response.data;
  },
};
